import React, { ReactNode } from 'react'
import { useIntl } from 'react-intl'

import styled from 'styled-components/native'

import { ButtonSize, ButtonType } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/baseButton/BaseButton'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { DropdownButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/dropdownButton/DropdownButton'
import {
  TypeAheadCustomOptionsConfig,
  ValueFetchMode,
} from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/typeAhead/TypeAheadCustomOptionsConfig'
import { CloseIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/CloseIcon'
import { MaximizeIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/MaximizeIcon'
import { TertiaryIconButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/tertiaryIconButton/TertiaryIconButton'
import { BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { ProviderPairStatusBadge } from './ProviderPairStatusBadge'
import { buildCoordinatedCareThreadSections } from '../data/coordinatedCareThreads'
import { MessengerParticipant } from '../data/types'

import type { MessengerThread } from '../data/types'

export type CompactMessengerHeaderProps = {
  threads: MessengerThread[]
  selectedThread: MessengerThread | null
  clientName?: string
  headerNote?: string
  onThreadSelected: (threadId: string) => void
  onExpand?: () => void
  onClose?: () => void
  dragHandle?: ReactNode
  testIDPrefix?: string
}

const Container = styled.View(({ theme }) => ({
  backgroundColor: theme.colors.backgroundSecondary,
  borderBottomWidth: 1,
  borderBottomColor: theme.colors.borderDefault,
  zIndex: 1,
}))
const Toolbar = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['4px'],
  paddingHorizontal: theme.spacing['8px'],
  paddingVertical: theme.spacing['4px'],
  minHeight: 40,
}))
const RecipientContainer = styled.View({ flex: 1, alignItems: 'flex-start' })
const RecipientDropdown = styled(DropdownButton).attrs(({ theme }) => ({
  containerStyle: { maxWidth: '100%' },
  customTextStyle: { flexShrink: 1, minWidth: 0 },
  contentContainerStyle: { alignSelf: 'stretch' },
  dropdownStyle: { maxHeight: 300, overflow: 'auto', borderWidth: 1, borderColor: theme.colors.borderDefault },
}))(({ theme }) => ({
  maxWidth: '100%',
  borderRadius: theme.spacing['24px'],
  backgroundColor: theme.colors.backgroundPrimary,
  borderWidth: 1,
  borderColor: theme.colors.borderDefault,
  paddingHorizontal: theme.spacing['8px'],
  minHeight: 28,
}))
const MenuItem = styled.View({ flex: 1 })
const HeaderNote = styled(BodyText)(({ theme }) => ({
  paddingHorizontal: theme.spacing['12px'],
  paddingBottom: theme.spacing['8px'],
  color: theme.colors.textSecondary,
}))

export const CompactMessengerHeader = ({
  threads,
  selectedThread,
  clientName,
  headerNote,
  onThreadSelected,
  onExpand,
  onClose,
  dragHandle,
  testIDPrefix = 'CompactMessenger',
}: CompactMessengerHeaderProps) => {
  const intl = useIntl()
  const { formatMessage } = intl
  const context = formatMessage(
    { defaultMessage: 'Messages for {clientName}', description: 'Context for the compact messenger recipient menu' },
    { clientName },
  )
  const recipient =
    (selectedThread?.participant === MessengerParticipant.AI
      ? selectedThread.tabLabel
      : selectedThread?.recipientName) ||
    formatMessage({
      defaultMessage: 'Loading messages…',
      description: 'Recipient label while loading the compact messenger',
    })
  const dropdownItems = [
    { id: 'context', text: context, menuItem_header: true, subText: '', selectHandler: () => {} },
    ...buildCoordinatedCareThreadSections(threads, intl).flatMap((section) =>
      section.threads.map((thread) => ({
        id: thread.id,
        text: thread.participantNames.join(', '),
        subText: thread.subtitle,
        menuItem_header: false,
        selectHandler: () => onThreadSelected(thread.id),
      })),
    ),
  ]
  return (
    <Container testID={tID(`${testIDPrefix}-header`)}>
      <Toolbar>
        {dragHandle}
        <RecipientContainer>
          <RecipientDropdown
            buttonType={ButtonType.SECONDARY}
            buttonSize={ButtonSize.EXTRA_SMALL}
            triggerTestID={tID(`${testIDPrefix}-recipient`)}
            dropdownTestID={tID(`${testIDPrefix}-threads`)}
            dropdownWidth={280}
            dropdownAccessibilityLabel={context}
            accessibilityLabel={formatMessage(
              {
                defaultMessage: '{recipient}. Change conversation for {clientName}',
                description: 'Compact messenger recipient switcher',
              },
              { recipient, clientName },
            )}
            customButtonText={recipient}
            customTextSize={BodyTextSize.CAPTION}
            buttonTextWidth='100%'
            shouldTruncateButtonText
            controlledValues={selectedThread ? [selectedThread.id] : []}
            dropdownItems={dropdownItems}
            customOptionsConfig={
              new TypeAheadCustomOptionsConfig(
                dropdownItems,
                (option, label) => (
                  <MenuItem>
                    {label}
                    {!option.menuItem_header && <BodyText text={option.subText} size={BodyTextSize.CAPTION} />}
                  </MenuItem>
                ),
                undefined,
                'text',
                'id',
                ValueFetchMode.ID,
              )
            }
            simpleHeaderStyle
            shouldCloseMenuOnOptionPress
            isDisabled={!selectedThread}
          />
        </RecipientContainer>
        {selectedThread?.providerPairStatus && <ProviderPairStatusBadge status={selectedThread.providerPairStatus} />}
        <TertiaryIconButton
          testID={tID(`${testIDPrefix}-expand`)}
          size={ButtonSize.SMALL}
          leftIcon={<MaximizeIcon size={16} />}
          disabled={!onExpand}
          onPress={() => onExpand?.()}
          accessibilityLabel={formatMessage({
            defaultMessage: 'Open conversation in Message Center',
            description: 'Expand compact messenger',
          })}
        />
        <TertiaryIconButton
          testID={tID(`${testIDPrefix}-close`)}
          size={ButtonSize.SMALL}
          leftIcon={<CloseIcon size={16} />}
          onPress={() => onClose?.()}
          accessibilityLabel={formatMessage({
            defaultMessage: 'Close messages',
            description: 'Close compact messenger',
          })}
        />
      </Toolbar>
      {headerNote && <HeaderNote text={headerNote} size={BodyTextSize.CAPTION} />}
    </Container>
  )
}
